import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  FiX, FiSend, FiRefreshCw, FiPhoneCall, 
  FiMail, FiMessageSquare, FiChevronRight, FiUser, 
  FiCheckCircle, FiZap, FiArrowRight
} from 'react-icons/fi';
import { TbRobot } from 'react-icons/tb';

// ─────────────────────────────────────────────────────────────────────────────
// 🧠 ROJRZTECH TRAINED KNOWLEDGE BASE & AI ENGINE
// ─────────────────────────────────────────────────────────────────────────────

const KNOWLEDGE_BASE = {
  company: {
    name: "RojrzTech LLC",
    tagline: "Innovating the Digital Future",
    location: "5900 Balcones Drive, STE 100, Austin, Texas, 78731",
    email: "info@rojrztech.com",
    phone: "+1 (512) 254-4193",
    whatsapp: "https://wa.me/15122544193",
    rating: "4.9/5 (2,300+ Clients Worldwide)",
    status: "LLC Registered Digital Solutions Agency"
  },
  services: [
    {
      name: "Web & Mobile Apps Development",
      desc: "Custom high-performance websites, React/Next.js web apps, iOS & Android mobile apps tailored for speed, scale, and high conversion.",
      starting: "$150"
    },
    {
      name: "CMS / E-Commerce Development",
      desc: "Shopify stores, WooCommerce, custom online stores with payment gateway integrations, stock management, and high-converting checkout funnels.",
      starting: "$150"
    },
    {
      name: "Digital Marketing & Strategy",
      desc: "Comprehensive SMM campaigns, meta ads, lead generation, brand awareness, and data-driven marketing strategies.",
      starting: "$50"
    },
    {
      name: "SEO (Search Engine Optimization)",
      desc: "Technical SEO audits, keyword research, on-page & off-page optimization, and high-authority link building to rank #1 on Google.",
      starting: "$150"
    },
    {
      name: "Graphic Design & Video Editing",
      desc: "Motion graphics, promotional video editing, corporate identity, brand kits, and social media post designs.",
      starting: "$49"
    },
    {
      name: "UI/UX Design",
      desc: "User research, interactive Figma prototypes, wireframing, and modern web/app UI design.",
      starting: "Custom Quote"
    },
    {
      name: "AI-Powered Technologies",
      desc: "Custom AI chatbots, automated customer workflows, machine learning models, and smart CRM integrations.",
      starting: "Custom Quote"
    },
    {
      name: "Logo Design",
      desc: "Unique custom logo design concepts by professional designers with 100% ownership rights.",
      starting: "$25"
    }
  ],
  pricing: [
    { category: "Logo Design", packages: "For Startups ($25), Entrepreneur ($55), Small Business ($105)" },
    { category: "Web Design", packages: "Startup Website ($150), Professional ($250), Custom Enterprise (Inquire)" },
    { category: "E-Commerce", packages: "Basic Store ($150), Startup Package ($200), Professional Store ($250)" },
    { category: "SEO", packages: "Startup SEO ($150), Professional SEO ($250), Custom SEO Strategy" },
    { category: "SMM", packages: "SMM Startup ($50), Pro Video Editing ($250), Custom Campaign" },
    { category: "2D/3D Animation", packages: "Basic Illustrative ($100), Startup Illustrative ($200)" },
    { category: "Content Marketing", packages: "Basic Web Content ($99), Starter ($300)" }
  ],
  process: [
    "1. Onboarding (Requirements discovery)",
    "2. REQ Gathering (Timeline & goal setting)",
    "3. Development (Custom coding & design)",
    "4. Quality Assurance (Rigorous testing & security checks)",
    "5. Going Live (Deployment & 24/7 post-launch support)"
  ]
};

// 🤖 Smart AI Response Algorithm
const generateAIResponse = (userMessage) => {
  const query = userMessage.toLowerCase().trim();

  // 1. GREETINGS & INTROS
  if (query.match(/\b(hi|hello|hey|assalam|salaam|greetings|good morning|good evening|hey there|aOA)\b/)) {
    return {
      text: "Hello! 👋 I am **RojrzAI**, the official virtual assistant for **RojrzTech LLC**.\n\nHow can I help you today? You can ask me about our **Web Development**, **Shopify Stores**, **Pricing**, or **Contact Details**!",
      actions: ["Services", "Pricing", "Contact Info"]
    };
  }

  // 2. ROMAN URDU / HINDI COMMON QUERIES
  if (query.match(/\b(rate|price|kitne|paisa|cost|kharcha|shuru|karo|batao|kya hai|kya karte ho|kaise ho|website banwani|service|pakistan|usa)\b/)) {
    if (query.includes('price') || query.includes('rate') || query.includes('cost') || query.includes('kitne')) {
      return {
        text: "Humari packages bohot flexible aur affordable hain! 🎯\n\n• **Logo Design:** $25 se shuru\n• **Web Design:** $150 se shuru\n• **E-Commerce / Shopify:** $150 se shuru\n• **SEO Packages:** $150 se shuru\n• **Social Media (SMM):** $50 se shuru\n\nAapko kis specific service ka package janna hai?",
        actions: ["Web Design Pricing", "Logo Packages", "Book a Free Call"]
      };
    }
    if (query.includes('website') || query.includes('banwani')) {
      return {
        text: "Zaroor! Hum professional, ultra-fast aur mobile-responsive websites banate hain using **React, Next.js, WordPress, and Shopify**! 🚀\n\n• **Startup Web Package:** $150\n• **Pro Business Web Package:** $250\n\nAap WhatsApp par direct humse project detail share kar sakte hain!",
        actions: ["WhatsApp Us", "Call +1 (512) 254-4193", "View Portfolio"]
      };
    }
  }

  // 3. PRICING & PACKAGES
  if (query.match(/\b(price|pricing|cost|package|rate|charge|fee|budget|how much)\b/)) {
    return {
      text: "Here is a quick breakdown of our most popular transparent pricing plans: 💳\n\n" +
        "• **Logo Design:** Starts at **$25** (3 concepts, 100% ownership)\n" +
        "• **Web Development:** Starts at **$150** (Responsive, SEO optimized)\n" +
        "• **Shopify / E-Commerce:** Starts at **$150** (Full store setup)\n" +
        "• **SEO Optimization:** Starts at **$150/mo** (Rank #1 on Google)\n" +
        "• **SMM & Video Editing:** Starts at **$50**\n\n" +
        "Would you like to discuss a custom budget for your project?",
      actions: ["Get Free Quote", "WhatsApp Us", "Call Us"]
    };
  }

  // 4. WEB DEVELOPMENT & MOBILE APPS
  if (query.match(/\b(web|website|app|mobile|react|nextjs|wordpress|application|frontend|backend|software|code)\b/)) {
    return {
      text: "At **RojrzTech**, we build cutting-edge web and mobile applications using **React, Next.js, Node.js, Python, and React Native**! 💻📱\n\n" +
        "✅ 100% Mobile Responsive & Fast Loading\n" +
        "✅ Clean Code & Bulletproof Security\n" +
        "✅ Integrated Lead Contact Forms & Analytics\n" +
        "✅ Ongoing Maintenance & 24/7 Technical Support\n\n" +
        "Starting from **$150**. Let's turn your vision into reality!",
      actions: ["Get Quote", "View Portfolio", "Book Consultation"]
    };
  }

  // 5. SHOPIFY & ECOMMERCE
  if (query.match(/\b(shopify|ecommerce|e-commerce|store|online shop|cart|woocommerce|products|sales)\b/)) {
    return {
      text: "We specialize in building high-converting **Shopify & E-Commerce Stores**! 🛒✨\n\n" +
        "• **Basic Store Package:** $150 (Essential store setup + payment gateways)\n" +
        "• **Startup Package:** $200 (Inventory tracking + multiple payment options)\n" +
        "• **Pro E-Commerce:** $250 (Unlimited product listings + advanced marketing funnels)\n\n" +
        "We optimize stores to maximize sales and customer checkout conversions.",
      actions: ["Order E-Commerce", "Talk to Expert", "WhatsApp Us"]
    };
  }

  // 6. LOGO & GRAPHIC DESIGN / BRANDING
  if (query.match(/\b(logo|design|graphic|branding|brand|video|animation|banner|ui|ux|figma)\b/)) {
    return {
      text: "Our design team crafts iconic visual identity for global brands! 🎨\n\n" +
        "• **Startup Logo ($25):** 3 concepts, 3 revisions, high-res PNG/JPG\n" +
        "• **Entrepreneur Logo ($55):** 4 custom concepts by 2 designers, unlimited revisions\n" +
        "• **Small Business ($105):** 12 concepts, 4 designers, full brand stationery\n" +
        "• **Video & Motion Graphics:** Starts at $49\n\n" +
        "You own 100% copyright ownership rights for all designs created!",
      actions: ["Order Logo", "View Portfolio", "Direct Email"]
    };
  }

  // 7. SEO & DIGITAL MARKETING
  if (query.match(/\b(seo|ranking|google|marketing|smm|social media|traffic|leads|ads|facebook|instagram)\b/)) {
    return {
      text: "Boost your organic reach and acquire high-intent clients with our **SEO & Digital Marketing** services! 📈\n\n" +
        "• **On-Page & Technical SEO:** Keyword research, site speed optimization, schema markup.\n" +
        "• **Off-Page SEO:** High DA backlink building & local GMB ranking.\n" +
        "• **SMM Campaigns:** Social media profile optimization, content strategy, & lead ads.\n\n" +
        "Packages start at **$50** for SMM and **$150** for full-scale SEO.",
      actions: ["SEO Audit", "Get Free Quote", "WhatsApp Us"]
    };
  }

  // 8. CONTACT / ADDRESS / PHONE / EMAIL / LOCATION
  if (query.match(/\b(contact|phone|email|number|address|location|office|where|call|reach|whatsapp|support)\b/)) {
    return {
      text: "You can reach the **RojrzTech** team anytime via: 📍\n\n" +
        "🏢 **Headquarters:** 5900 Balcones Drive, STE 100, Austin, Texas, 78731\n" +
        "📞 **Phone:** +1 (512) 254-4193\n" +
        "📧 **Email:** info@rojrztech.com\n" +
        "💬 **WhatsApp:** +1 (512) 254-4193\n" +
        "🛡️ **Legal:** Registered LLC Company in USA",
      actions: ["Call Now", "WhatsApp Chat", "Send Email"]
    };
  }

  // 9. PROCESS & HOW WE WORK
  if (query.match(/\b(process|work|step|how it works|method|timeline|delivery|turnaround)\b/)) {
    return {
      text: "Our streamlined 5-step engineering process ensures top quality: ⚙️\n\n" +
        "1. **Onboarding:** Discovery call & listening to your requirements.\n" +
        "2. **REQ Gathering:** Scope definition, milestones & timeline setup.\n" +
        "3. **Development:** Crafting high-quality code & modern UI.\n" +
        "4. **Quality Assurance:** Rigorous multi-device testing & security checks.\n" +
        "5. **Going Live:** Smooth deployment & 24/7 post-launch support.",
      actions: ["Start Project", "Contact Us", "WhatsApp Us"]
    };
  }

  // 10. DEFAULT / GENERAL SMART FALLBACK
  return {
    text: "Thank you for reaching out! 🚀 As the AI Assistant for **RojrzTech LLC**, I can assist you with:\n\n" +
      "1. **Web & Mobile App Development** ($150+)\n" +
      "2. **Shopify & E-Commerce Solutions** ($150+)\n" +
      "3. **Logo Design & Branding** ($25+)\n" +
      "4. **SEO & Social Media Marketing** ($50+)\n\n" +
      "Would you like to connect directly with our senior technology team via WhatsApp or Call?",
    actions: ["WhatsApp Us", "Call +1 (512) 254-4193", "Get Free Quote"]
  };
};

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'bot',
      text: "Hi there! 👋 Welcome to **RojrzTech LLC**.\nI am your trained **AI Assistant**. How can I help you grow your business today?",
      actions: ["Services", "Pricing", "Contact Info"],
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(1);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setUnreadCount(0);
    }
  }, [messages, isOpen]);

  // Handle Action Button Clicks inside Chat
  const handleActionClick = (actionText) => {
    if (actionText === "WhatsApp Us" || actionText === "WhatsApp Chat") {
      window.open(KNOWLEDGE_BASE.company.whatsapp, '_blank');
      return;
    }
    if (actionText.includes("Call") || actionText === "Call Now") {
      window.location.href = `tel:${KNOWLEDGE_BASE.company.phone}`;
      return;
    }
    if (actionText === "Direct Email" || actionText === "Send Email") {
      window.location.href = `mailto:${KNOWLEDGE_BASE.company.email}`;
      return;
    }
    if (actionText === "View Portfolio") {
      window.location.href = "/portfolio";
      return;
    }
    if (actionText === "Contact Info" || actionText === "Contact Us") {
      window.location.href = "/contact";
      return;
    }

    // Send action as user prompt
    handleSendMessage(actionText);
  };

  // Handle Sending Message
// 🌐 Free Real-Time Human AI Integration (Pollinations API + Local Trained Engine Fallback)
const SYSTEM_PROMPT = `You are Sarah, a warm, helpful, senior human AI consultant at RojrzTech LLC in Austin, Texas. 
Speak naturally, warmly, and helpfully like a real human team member. 
Language rules:
- If the user speaks in English, reply in friendly English.
- If the user speaks in Roman Urdu or Urdu (e.g. "kya rate hai", "website banwani hai", "batao", "kaise ho", "bhai"), reply naturally in friendly Roman Urdu / English mix!
- Keep replies concise (2-4 paragraphs max), polite, and human-like.
RojrzTech details:
- Services: Web & Mobile App Dev ($150+), Shopify & E-commerce ($150+), Logo Design ($25+), SEO ($150+), SMM ($50+), 2D/3D Animation ($100+), Content Marketing ($99+).
- Contact: Email info@rojrztech.com, Phone/WhatsApp +1 (512) 254-4193, Address: 5900 Balcones Dr, STE 100, Austin, TX.
- Guarantees: 100% satisfaction, 100% money back guarantee on select packages, registered US LLC.`;

const fetchLiveAIResponse = async (userText) => {
  try {
    const fullPrompt = SYSTEM_PROMPT + "\n\nUser Question: " + userText + "\n\nHuman-like Response:";
    const response = await fetch(`https://text.pollinations.ai/${encodeURIComponent(fullPrompt)}?model=openai&cache=false`);
    if (response.ok) {
      const text = await response.text();
      if (text && text.trim().length > 5) {
        return {
          text: text.trim(),
          actions: ["WhatsApp Us", "Call +1 (512) 254-4193", "Get Free Quote"]
        };
      }
    }
  } catch (err) {
    console.warn("Live API fallback active:", err);
  }
  return generateAIResponse(userText);
};

  const handleSendMessage = async (textToSend = null) => {
    const text = textToSend || inputValue;
    if (!text.trim()) return;

    const userMsg = {
      id: Date.now(),
      sender: 'user',
      text: text.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!textToSend) setInputValue('');
    setIsTyping(true);

    try {
      const response = await fetchLiveAIResponse(text.trim());
      const botMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: response.text,
        actions: response.actions || ["WhatsApp Us", "Call Us"],
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      const response = generateAIResponse(text.trim());
      const botMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: response.text,
        actions: response.actions,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, botMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleResetChat = () => {
    setMessages([
      {
        id: Date.now(),
        sender: 'bot',
        text: "Chat reset! 👋 How else can I assist you with **RojrzTech** services?",
        actions: ["Services", "Pricing", "Contact Info"],
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  };

  const location = useLocation();

  // Hide AI chatbot in Admin Dashboard to prevent layout overlap
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <>
      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* 🌟 FLOATING TRIGGER BUTTON (BOTTOM-LEFT CORNER)                    */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div className="fixed bottom-5 left-5 z-[9999] flex items-center gap-3 select-none">
        
        {/* Trigger Circle Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle AI Assistant Chat"
          className="relative group btn-animated-gold !p-0 w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(242,177,53,0.4)] cursor-pointer transition-all duration-300 hover:scale-110 active:scale-95"
        >
          {/* Pulsating Online Outer Aura Ring */}
          <span className="absolute -inset-1 rounded-full bg-[#F2B135]/40 animate-ping duration-1000 pointer-events-none" />

          {/* Unread Badge */}
          {unreadCount > 0 && !isOpen && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-black animate-bounce shadow-md">
              {unreadCount}
            </span>
          )}

          {/* Icon */}
          {isOpen ? (
            <FiX size={26} className="text-black transition-transform duration-300 rotate-90" />
          ) : (
            <TbRobot size={28} className="text-black transition-transform duration-300 group-hover:rotate-12" />
          )}
        </button>

        {/* Hover Pill Label */}
        {!isOpen && (
          <div 
            onClick={() => setIsOpen(true)}
            className="hidden sm:flex items-center gap-2 bg-black/90 backdrop-blur-md border border-[#F2B135]/40 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-2xl cursor-pointer hover:border-[#F2B135] transition-all"
          >
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[#F2B135]">RojrzAI</span> Assistant
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* 💬 MAIN AI CHAT WINDOW CONTAINER                                   */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {isOpen && (
        <div 
          className="fixed bottom-22 left-4 sm:left-5 z-[99999] w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[80vh] rounded-3xl bg-black/95 border-2 border-[#F2B135]/50 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.95),0_0_40px_rgba(242,177,53,0.2)] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-300 select-none"
        >
          {/* ─── 1. CHAT HEADER ─── */}
          <div className="p-4 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 border-b border-[#F2B135]/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Bot Avatar Icon */}
              <div className="relative w-10 h-10 rounded-full bg-gradient-to-tr from-[#F2B135] to-[#ffaa00] flex items-center justify-center text-black font-black shadow-md">
                <TbRobot size={22} />
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-black rounded-full" />
              </div>

              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-white font-extrabold text-sm tracking-wide font-display">
                    Rojrz<span className="text-[#F2B135]">AI</span>
                  </h3>
                  <span className="bg-[#F2B135]/15 border border-[#F2B135]/40 text-[#F2B135] text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider">
                    PRO
                  </span>
                </div>
                <p className="text-[10px] text-zinc-400 font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Online & Trained Agent
                </p>
              </div>
            </div>

            {/* Header Controls */}
            <div className="flex items-center gap-1">
              {/* Reset Chat */}
              <button
                onClick={handleResetChat}
                title="Reset Chat"
                className="p-2 text-zinc-400 hover:text-[#F2B135] transition-colors rounded-lg hover:bg-white/5"
              >
                <FiRefreshCw size={15} />
              </button>
              {/* Close */}
              <button
                onClick={() => setIsOpen(false)}
                title="Close Chat"
                className="p-2 text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              >
                <FiX size={18} />
              </button>
            </div>
          </div>

          {/* ─── 2. SUGGESTED QUICK PROMPT PILLS ─── */}
          <div className="px-3 py-2 bg-zinc-950/80 border-b border-zinc-900 overflow-x-auto flex gap-2 scrollbar-hide">
            {[
              { label: "🚀 Services", query: "What services do you offer?" },
              { label: "💰 Pricing", query: "What are your pricing packages?" },
              { label: "📱 Web & Apps", query: "How much for a custom website or mobile app?" },
              { label: "🛍️ Shopify", query: "Do you build Shopify & E-commerce stores?" },
              { label: "🎨 Logo & Branding", query: "How much does a logo design cost?" },
              { label: "📞 Contact Us", query: "Where is your office and phone number?" }
            ].map((pill, idx) => (
              <button
                key={idx}
                onClick={() => handleSendMessage(pill.query)}
                className="flex-none whitespace-nowrap px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 hover:border-[#F2B135] hover:text-[#F2B135] text-zinc-300 text-[10px] font-semibold transition-all"
              >
                {pill.label}
              </button>
            ))}
          </div>

          {/* ─── 3. MESSAGES HISTORY STREAM ─── */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-transparent scrollbar-hide">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'} animate-in fade-in duration-200`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-3.5 text-xs leading-relaxed ${
                    msg.sender === 'user'
                      ? 'bg-gradient-to-r from-[#F2B135] to-[#ffaa00] text-black font-semibold rounded-br-none shadow-md'
                      : 'bg-zinc-900/90 border border-zinc-800 text-zinc-200 rounded-bl-none shadow-lg'
                  }`}
                >
                  {/* Message Content Formatting (bold rendering support) */}
                  <div className="whitespace-pre-line">
                    {msg.text.split('**').map((part, index) =>
                      index % 2 === 1 ? <strong key={index} className={msg.sender === 'user' ? 'text-black font-black' : 'text-[#F2B135] font-black'}>{part}</strong> : part
                    )}
                  </div>

                  {/* Bot Interactive Action Buttons */}
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-zinc-800 flex flex-wrap gap-1.5">
                      {msg.actions.map((act, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleActionClick(act)}
                          className="px-2.5 py-1 rounded-lg bg-[#F2B135]/10 border border-[#F2B135]/30 hover:bg-[#F2B135] hover:text-black text-[#F2B135] text-[10px] font-bold transition-all flex items-center gap-1 active:scale-95"
                        >
                          <span>{act}</span>
                          <FiChevronRight size={10} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <span className="text-[9px] text-zinc-600 font-medium mt-1 px-1">
                  {msg.time}
                </span>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex items-center gap-2 bg-zinc-900/80 border border-zinc-800 text-zinc-400 p-3 rounded-2xl rounded-bl-none max-w-[100px]">
                <span className="w-1.5 h-1.5 bg-[#F2B135] rounded-full animate-bounce" />
                <span className="w-1.5 h-1.5 bg-[#F2B135] rounded-full animate-bounce [animation-delay:0.2s]" />
                <span className="w-1.5 h-1.5 bg-[#F2B135] rounded-full animate-bounce [animation-delay:0.4s]" />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ─── 4. CHAT INPUT BAR ─── */}
          <div className="p-3 bg-zinc-950 border-t border-zinc-900 flex items-center gap-2">
            <input
              type="text"
              placeholder="Ask RojrzAI anything..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              className="flex-1 px-4 py-2.5 rounded-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 text-xs font-medium focus:outline-none focus:border-[#F2B135] transition-all"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim()}
              className="w-9 h-9 rounded-full bg-[#F2B135] hover:bg-[#ffaa00] text-black flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md shrink-0 active:scale-95"
            >
              <FiSend size={15} />
            </button>
          </div>

        </div>
      )}
    </>
  );
};

export default AIChatbot;
